/**
 * 7 · Negozio — caricare e chiedere merce sfusa.
 *
 * La cosa che questa schermata deve far capire in tre secondi è che **qui non
 * ci sono cassette**: si carica «trenta pezzi di questo articolo», e l'articolo
 * è la variante di DecoNetwork — quella con la taglia dentro. È l'unico modo in
 * cui un prelievo di «tre nere taglia M» si possa poi chiedere.
 *
 * Due scelte che vale la pena difendere:
 *
 * - **la chiave non si digita mai.** Si cerca per quello che si legge sul capo —
 *   nome, colore, taglia, codice — e la riga che si tocca porta con sé la
 *   chiave. Comporla a mano vorrebbe dire poter registrare merce sotto un
 *   codice che DecoNetwork non riconosce, e quella merce non la ritrova più
 *   nessuno;
 * - **«mai letto» non è «zero».** Finché i Modula non sono installati la
 *   giacenza è nulla, e uno zero a video farebbe sembrare vuoto un magazzino
 *   che nessuno ha interrogato. *
 * Since 25/09/2026 the same row also feeds the shop shelf (rules 42–43): «Rifornisci
 * scaffale» picks from the Modula without an order, «Metti a scaffale» loads goods
 * that never entered the machine.
 */

import { useState } from 'react';

import { Notifiche } from '../components/notifications';
import { Sezione } from '../components/section';
import { Stato } from '../components/state';
import { Spazio, Testata, Voce } from '../components/header';
import { Avviso, useFlusso } from '../flow-api';

interface Articolo {
  variante: string;
  codice: string;
  articolo: string;
  colore: string;
  taglia: string;
}
interface RigaBaia extends Articolo {
  capi: number;
}
interface VistaNegozio {
  baia: RigaBaia[];
  /** `null` = il magazzino non ha parlato. **Non** è zero. */
  giacenza: Record<string, number> | null;
  missioni: { nome: string; tipo: string; righe?: { variante: string; capi: number }[] }[];
}

export function Negozio() {
  const [cerca, setCerca] = useState('');
  const [quanti, setQuanti] = useState(1);

  const { dati, caricando, ultimo, inCorso, fai } = useFlusso<VistaNegozio>('negozio', '/negozio');
  // La chiave porta dentro la parola cercata: cambiandola cambia la query, e la
  // ricerca si rifà da sé senza un secondo meccanismo da tenere in piedi.
  const { dati: trovati } = useFlusso<Articolo[]>(
    `negozio-cerca-${cerca}`,
    `/negozio/cerca?q=${encodeURIComponent(cerca)}`,
  );

  if (caricando || !dati) return <Stato quale="caricando" dice="Apro il negozio…" />;

  const pezzi = dati.baia.reduce((n, r) => n + r.capi, 0);
  const dentro = (variante: string): string => {
    if (dati.giacenza === null) return 'mai letto';
    return String(dati.giacenza[variante] ?? 0);
  };

  return (
    <div className="shell">
      <Testata>
        <Voce k="Baia negozio" v={`${dati.baia.length} righe · ${pezzi} pezzi`} />
        <Voce k="Nel Modula" v={dati.giacenza === null ? 'mai letto' : 'letto'} />
        <Spazio />
        <button
          className="btn btn--primario"
          disabled={inCorso || dati.baia.length === 0}
          onClick={fai('/negozio/conferma')}
        >
          Conferma carico
        </button>
        <Notifiche />
      </Testata>

      <div className="main">
        <section className="booth" style={{ background: 'var(--panel)' }}>
          <Sezione titolo="Cerca l'articolo">
            {/* `.cerca__campo` e non un input nudo: al banco si legge col
                lettore, e quella classe è già il campo grande della ricezione. */}
            <input
              className="cerca__campo"
              style={{ width: '100%' }}
              value={cerca}
              onChange={(e) => setCerca(e.target.value)}
              placeholder="nome, colore, taglia o codice"
              aria-label="cerca articolo"
            />

            {/*
              Il contatore sta **fuori** dalle righe e non dentro ognuna: al
              banco si conta una cosa alla volta, e un contatore per riga
              sarebbe uno stato in più da guardare per ogni articolo.
            */}
            <div className="mods" style={{ marginTop: 12, alignItems: 'center' }}>
              <button
                className="mod"
                aria-label="meno uno"
                onClick={() => setQuanti((n) => Math.max(1, n - 1))}
              >
                −
              </button>
              <span className="riga__conto" style={{ minWidth: 48, textAlign: 'center' }}>
                {quanti}
              </span>
              <button className="mod" aria-label="più uno" onClick={() => setQuanti((n) => n + 1)}>
                +
              </button>
              <span className="mod__hint">pezzi per volta</span>
            </div>

            <div className="righe" style={{ marginTop: 12 }}>
              {(trovati ?? []).map((a) => (
                <article key={a.variante} className="riga">
                  <div className="riga__cliente">
                    {a.articolo} · {a.colore}
                  </div>
                  <div className="riga__conto">
                    <span>{a.taglia}</span>
                  </div>
                  <div className="riga__sku" style={{ fontFamily: 'var(--mono)' }}>
                    {a.variante}
                  </div>
                  <div className="mod__hint">nel Modula: {dentro(a.variante)}</div>
                  <div className="mods" style={{ marginTop: 8 }}>
                    <button
                      className="mod"
                      disabled={inCorso}
                      onClick={fai('/negozio/baia', { variante: a.variante, capi: quanti })}
                    >
                      Carica ×{quanti}
                    </button>
                    {/* A pick without an order goes to the shop shelf (rule 42),
                        with what the Modula says it handed over. */}
                    <button
                      className="mod mod--quiet"
                      disabled={inCorso}
                      onClick={fai('/negozio/preleva', { variante: a.variante, capi: quanti })}
                    >
                      Rifornisci scaffale ×{quanti}
                    </button>
                    {/* Straight from the supplier to the shelf (rule 43). */}
                    <button
                      className="mod mod--quiet"
                      disabled={inCorso}
                      onClick={fai('/negozio/scaffale/carico', {
                        variante: a.variante,
                        capi: quanti,
                      })}
                    >
                      Metti a scaffale ×{quanti}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </Sezione>
        </section>

        <section className="righe">
          <Sezione titolo="In baia adesso">
            {dati.baia.length === 0 ? (
              <p className="booth__vuoto">
                Cerca l’articolo che stai mettendo dentro e di’ quanti pezzi sono. Finché non
                confermi, per Modula non ci sono.
              </p>
            ) : (
              dati.baia.map((r) => (
                <article key={r.variante} className="riga riga--att">
                  <div className="riga__cliente">
                    {r.articolo} · {r.colore} · {r.taglia}
                  </div>
                  <div className="riga__conto">{r.capi} pezzi</div>
                  <div className="mods" style={{ marginTop: 8 }}>
                    <button
                      className="mod mod--quiet"
                      disabled={inCorso}
                      onClick={fai('/negozio/baia/togli', { variante: r.variante })}
                    >
                      Togli
                    </button>
                  </div>
                </article>
              ))
            )}
          </Sezione>

          {/*
            Le ultime missioni, col nome che compare anche sul WMS: è la riga
            che permette a chi sta al telefono col fornitore di dire *quale*
            missione sta guardando.
          */}
          {dati.missioni.length > 0 && (
            <Sezione titolo="Ultime missioni">
              {dati.missioni.map((m) => (
                <div key={m.nome} className="mod__hint" style={{ fontFamily: 'var(--mono)' }}>
                  {m.nome} · {m.tipo === 'V' ? 'carico' : 'prelievo'} ·{' '}
                  {(m.righe ?? []).reduce((n, r) => n + r.capi, 0)} pezzi
                </div>
              ))}
            </Sezione>
          )}
        </section>
      </div>

      <Avviso esito={ultimo} />
    </div>
  );
}
